import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import EquityCurve from './EquityCurve';

function calcR(entry, stop, exit, direction) {
  const e = parseFloat(entry), s = parseFloat(stop), x = parseFloat(exit);
  if (!e || !s || !x) return null;
  const risk = Math.abs(e - s);
  if (risk === 0) return null;
  const gain = direction === 'Long' ? x - e : e - x;
  return parseFloat((gain / risk).toFixed(3));
}

const empty = {
  trade_date: new Date().toISOString().slice(0, 10),
  direction: 'Long',
  session: 'London',
  entry_price: '',
  stop_loss: '',
  exit_price: '',
  outcome: 'Win',
  setup_type: '',
  checklist_complete: 'Yes',
  tags: '',
  emotional_state: '',
  notes: ''
};

export default function Journal() {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({ ...empty });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [showCurve, setShowCurve] = useState(true);
  const perPage = 20;

  // Filters
  const [filterOutcome, setFilterOutcome] = useState('All');
  const [filterDirection, setFilterDirection] = useState('All');
  const [filterSession, setFilterSession] = useState('All');
  const [filterTag, setFilterTag] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', user.id)
      .order('trade_date', { ascending: false });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setTrades(data || []);
  }, [user.id]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    setSubmitting(true);

    const r = calcR(form.entry_price, form.stop_loss, form.exit_price, form.direction);

    const payload = {
      user_id: user.id,
      trade_date: form.trade_date,
      direction: form.direction,
      session: form.session,
      entry_price: parseFloat(form.entry_price) || null,
      stop_loss: parseFloat(form.stop_loss) || null,
      exit_price: parseFloat(form.exit_price) || null,
      r_multiple: r,
      outcome: form.outcome,
      setup_type: form.setup_type || null,
      checklist_complete: form.checklist_complete,
      tags: form.tags || null,
      emotional_state: form.emotional_state || null,
      notes: form.notes || null
    };

    let res;
    if (editingId) {
      res = await supabase.from('trades').update(payload).eq('id', editingId);
    } else {
      res = await supabase.from('trades').insert(payload);
    }

    setSubmitting(false);

    if (res.error) {
      setError('Failed to save: ' + res.error.message);
      return;
    }
    setSuccess(editingId ? 'Trade updated.' : 'Trade logged successfully.');
    setForm({ ...empty });
    setEditingId(null);
    fetchTrades();
    setTimeout(() => setSuccess(''), 3000);
  }

  function editTrade(t) {
    setForm({
      trade_date: t.trade_date,
      direction: t.direction || 'Long',
      session: t.session || 'London',
      entry_price: t.entry_price || '',
      stop_loss: t.stop_loss || '',
      exit_price: t.exit_price || '',
      outcome: t.outcome || 'Win',
      setup_type: t.setup_type || '',
      checklist_complete: t.checklist_complete || 'Yes',
      tags: t.tags || '',
      emotional_state: t.emotional_state || '',
      notes: t.notes || ''
    });
    setEditingId(t.id);
    window.scrollTo({top:0, behavior:'smooth'});
  }

  async function deleteTrade(id) {
    if (!window.confirm('Delete this trade?')) return;
    const { error } = await supabase.from('trades').delete().eq('id', id);
    if (error) { setError(error.message); return; }
    setTrades(prev => prev.filter(t => t.id !== id));
  }

  function exportCSV() {
    const header = ['Date','Direction','Session','Entry','Stop','Exit','R','Outcome','Setup','Checklist','Tags','Emotional','Notes'];
    const rows = trades.map(t => [
      t.trade_date, t.direction, t.session || '', t.entry_price || '', t.stop_loss || '',
      t.exit_price || '', t.r_multiple || '', t.outcome || '', t.setup_type || '',
      t.checklist_complete || '', t.tags || '', t.emotional_state || '', (t.notes || '').replace(/,/g, ';')
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'trades.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  function clearFilters() {
    setFilterOutcome('All'); setFilterDirection('All'); setFilterSession('All');
    setFilterTag(''); setFilterDateFrom(''); setFilterDateTo('');
  }

  const filtered = useMemo(() => {
    return trades.filter(t => {
      if (filterOutcome !== 'All' && t.outcome !== filterOutcome) return false;
      if (filterDirection !== 'All' && t.direction !== filterDirection) return false;
      if (filterSession !== 'All' && t.session !== filterSession) return false;
      if (filterTag && !(t.tags || '').toLowerCase().includes(filterTag.toLowerCase()) &&
          !(t.setup_type || '').toLowerCase().includes(filterTag.toLowerCase())) return false;
      if (filterDateFrom && t.trade_date < filterDateFrom) return false;
      if (filterDateTo && t.trade_date > filterDateTo) return false;
      return true;
    });
  }, [trades, filterOutcome, filterDirection, filterSession, filterTag, filterDateFrom, filterDateTo]);

  const wins = filtered.filter(t => t.outcome === 'Win').length;
  const losses = filtered.filter(t => t.outcome === 'Loss').length;
  const winRate = filtered.length > 0 ? ((wins / filtered.length) * 100).toFixed(1) : '—';
  const rVals = filtered.filter(t => t.r_multiple !== null).map(t => t.r_multiple);
  const avgR = rVals.length > 0 ? (rVals.reduce((a, b) => a + b, 0) / rVals.length).toFixed(2) : '—';
  const totalRNum = rVals.length > 0 ? rVals.reduce((a, b) => a + b, 0) : 0;
  const totalR = rVals.length > 0 ? totalRNum.toFixed(2) : '—';
  const expectancy = rVals.length > 0 && filtered.length > 0
    ? ((wins / filtered.length * parseFloat(avgR)) - (losses / filtered.length * 1)).toFixed(2)
    : '—';

  const paginated = filtered.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);
  const previewR = calcR(form.entry_price, form.stop_loss, form.exit_price, form.direction);

  const hasFilters = filterOutcome !== 'All' || filterDirection !== 'All' || filterSession !== 'All' || filterTag || filterDateFrom || filterDateTo;

  return (
    <div>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h1 className="page-title"><em>Trade Journal</em></h1>
            <p className="page-subtitle">Your private XAUUSD trade log — {filtered.length} of {trades.length} trades shown</p>
          </div>
          {trades.length > 0 && (
            <div style={{display:'flex',gap:8}}>
              <button className="btn-sm ghost" onClick={() => setShowCurve(!showCurve)}>
                {showCurve ? 'Hide' : 'Show'} Equity Curve
              </button>
              <button className="btn-sm ghost" onClick={exportCSV}>Export CSV</button>
            </div>
          )}
        </div>
      </div>

      {trades.length >= 2 && showCurve && (
        <div className="card mb-24">
          <div className="card-title">EQUITY CURVE</div>
          <EquityCurve trades={filtered.length > 0 ? filtered : trades} height={220} />
        </div>
      )}

      <div className="journal-stats">
        {[
          { label: 'TRADES', val: filtered.length },
          { label: 'WIN RATE', val: filtered.length ? winRate + '%' : '—' },
          { label: 'AVG R', val: avgR },
          { label: 'TOTAL R', val: totalR !== '—' ? (totalRNum > 0 ? '+' : '') + totalR : '—' },
          { label: 'EXPECTANCY', val: expectancy },
        ].map(s => (
          <div key={s.label} className="kpi-cell">
            <div className="kpi-label">{s.label}</div>
            <div className="kpi-value">{s.val}</div>
          </div>
        ))}
      </div>

      <div className="card mb-24">
        <div className="card-title">{editingId ? 'EDIT TRADE' : 'LOG NEW TRADE'}</div>
        {error && <div className="error-banner">{error}</div>}
        {success && <div className="success-banner">{success}</div>}
        <form className="journal-form" onSubmit={handleSubmit}>
          <div className="field">
            <label>DATE</label>
            <input type="date" value={form.trade_date} onChange={e => setForm(f => ({...f, trade_date: e.target.value}))} required />
          </div>
          <div className="field">
            <label>DIRECTION</label>
            <select value={form.direction} onChange={e => setForm(f => ({...f, direction: e.target.value}))}>
              <option value="Long">Long</option>
              <option value="Short">Short</option>
            </select>
          </div>
          <div className="field">
            <label>SESSION</label>
            <select value={form.session} onChange={e => setForm(f => ({...f, session: e.target.value}))}>
              <option>London</option>
              <option>New York</option>
              <option>NY Overlap</option>
              <option>Asia</option>
              <option>Other</option>
            </select>
          </div>
          <div className="field">
            <label>ENTRY PRICE</label>
            <input type="number" step="0.01" placeholder="2345.50" value={form.entry_price} onChange={e => setForm(f => ({...f, entry_price: e.target.value}))} />
          </div>
          <div className="field">
            <label>STOP LOSS</label>
            <input type="number" step="0.01" placeholder="2330.00" value={form.stop_loss} onChange={e => setForm(f => ({...f, stop_loss: e.target.value}))} />
          </div>
          <div className="field">
            <label>EXIT PRICE</label>
            <input type="number" step="0.01" placeholder="2380.00" value={form.exit_price} onChange={e => setForm(f => ({...f, exit_price: e.target.value}))} />
          </div>
          <div className="field">
            <label>OUTCOME</label>
            <select value={form.outcome} onChange={e => setForm(f => ({...f, outcome: e.target.value}))}>
              <option value="Win">Win</option>
              <option value="Loss">Loss</option>
              <option value="Scratch">Scratch</option>
            </select>
          </div>
          <div className="field">
            <label>SETUP TYPE</label>
            <input type="text" placeholder="e.g. Liquidity Sweep + CHoCH" value={form.setup_type} onChange={e => setForm(f => ({...f, setup_type: e.target.value}))} />
          </div>
          <div className="field">
            <label>CHECKLIST</label>
            <select value={form.checklist_complete} onChange={e => setForm(f => ({...f, checklist_complete: e.target.value}))}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </div>
          <div className="field">
            <label>AUTO R-MULTIPLE</label>
            <input
              readOnly
              value={previewR !== null ? `${previewR}R` : '— (fill entry, stop, exit)'}
              style={{color: previewR !== null ? (previewR > 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-4)', cursor:'default'}}
            />
          </div>
          <div className="field">
            <label>TAGS (comma-separated)</label>
            <input type="text" placeholder="A+ setup, news driven, revenge" value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} />
          </div>
          <div className="field">
            <label>EMOTIONAL STATE</label>
            <select value={form.emotional_state} onChange={e => setForm(f => ({...f, emotional_state: e.target.value}))}>
              <option value="">— Optional —</option>
              <option value="Calm">Calm / Disciplined</option>
              <option value="Confident">Confident</option>
              <option value="Fearful">Fearful / Hesitant</option>
              <option value="FOMO">FOMO</option>
              <option value="Revenge">Revenge Trading</option>
              <option value="Tired">Tired / Distracted</option>
            </select>
          </div>
          <div className="field full">
            <label>NOTES / LESSON LEARNED</label>
            <textarea placeholder="What did you observe? What would you do differently?" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
          <div style={{gridColumn:'1/-1',display:'flex',gap:8}}>
            <button type="submit" className="btn-gold" disabled={submitting}>
              {submitting ? 'Saving...' : editingId ? 'Update Trade' : 'Log Trade'}
            </button>
            {editingId && (
              <button type="button" className="btn-sm ghost" onClick={() => { setEditingId(null); setForm({...empty}); }}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card mb-24">
        <div className="flex-between mb-16">
          <div className="card-title" style={{marginBottom:0}}>FILTERS</div>
          {hasFilters && <button className="btn-sm ghost" onClick={clearFilters}>Clear All</button>}
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12}}>
          <div className="field">
            <label style={{fontSize:10,color:'var(--text-3)',fontFamily:'DM Mono,monospace',letterSpacing:1}}>OUTCOME</label>
            <select value={filterOutcome} onChange={e => setFilterOutcome(e.target.value)}>
              <option>All</option><option>Win</option><option>Loss</option><option>Scratch</option>
            </select>
          </div>
          <div className="field">
            <label style={{fontSize:10,color:'var(--text-3)',fontFamily:'DM Mono,monospace',letterSpacing:1}}>DIRECTION</label>
            <select value={filterDirection} onChange={e => setFilterDirection(e.target.value)}>
              <option>All</option><option>Long</option><option>Short</option>
            </select>
          </div>
          <div className="field">
            <label style={{fontSize:10,color:'var(--text-3)',fontFamily:'DM Mono,monospace',letterSpacing:1}}>SESSION</label>
            <select value={filterSession} onChange={e => setFilterSession(e.target.value)}>
              <option>All</option><option>London</option><option>New York</option><option>NY Overlap</option><option>Asia</option><option>Other</option>
            </select>
          </div>
          <div className="field">
            <label style={{fontSize:10,color:'var(--text-3)',fontFamily:'DM Mono,monospace',letterSpacing:1}}>TAG / SETUP</label>
            <input type="text" placeholder="Search..." value={filterTag} onChange={e => setFilterTag(e.target.value)} />
          </div>
          <div className="field">
            <label style={{fontSize:10,color:'var(--text-3)',fontFamily:'DM Mono,monospace',letterSpacing:1}}>FROM DATE</label>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
          </div>
          <div className="field">
            <label style={{fontSize:10,color:'var(--text-3)',fontFamily:'DM Mono,monospace',letterSpacing:1}}>TO DATE</label>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex-between mb-16">
          <div className="card-title" style={{marginBottom:0}}>TRADE HISTORY</div>
          {totalPages > 1 && (
            <div className="flex-center gap-8">
              <button className="btn-sm ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}>←</button>
              <span style={{fontSize:12, color:'var(--text-3)', fontFamily:'DM Mono,monospace'}}>{page+1}/{totalPages}</span>
              <button className="btn-sm ghost" disabled={page >= totalPages-1} onClick={() => setPage(p => p + 1)}>→</button>
            </div>
          )}
        </div>
        {loading ? (
          <div style={{textAlign:'center', padding:'32px', color:'var(--text-3)'}}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{textAlign:'center', padding:'48px', color:'var(--text-4)'}}>
            <div style={{fontSize:32, marginBottom:12}}>📓</div>
            <p>{trades.length === 0 ? 'No trades logged yet.' : 'No trades match your filters.'}</p>
          </div>
        ) : (
          <div className="trades-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Dir</th><th>Session</th>
                  <th>Entry</th><th>Stop</th><th>Exit</th>
                  <th>R</th><th>Outcome</th><th>Setup</th>
                  <th>Tags</th><th>State</th><th>Check</th><th></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(t => (
                  <tr key={t.id}>
                    <td className="td-mono">{t.trade_date}</td>
                    <td><span className={`badge ${t.direction === 'Long' ? 'green' : 'red'}`}>{t.direction}</span></td>
                    <td style={{color:'var(--text-3)',fontSize:12}}>{t.session}</td>
                    <td className="td-mono">{t.entry_price}</td>
                    <td className="td-mono">{t.stop_loss}</td>
                    <td className="td-mono">{t.exit_price}</td>
                    <td className={t.r_multiple > 0 ? 'td-pos' : t.r_multiple < 0 ? 'td-neg' : 'td-mono'}>
                      {t.r_multiple !== null ? `${t.r_multiple}R` : '—'}
                    </td>
                    <td>
                      <span className={`badge ${t.outcome === 'Win' ? 'green' : t.outcome === 'Loss' ? 'red' : 'grey'}`}>{t.outcome}</span>
                    </td>
                    <td style={{maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', fontSize:12}}>{t.setup_type || '—'}</td>
                    <td style={{maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', fontSize:11, color:'var(--gold-3)'}}>{t.tags || '—'}</td>
                    <td style={{fontSize:11,color:'var(--text-3)'}}>{t.emotional_state || '—'}</td>
                    <td>
                      <span className={`badge ${t.checklist_complete === 'Yes' ? 'green' : t.checklist_complete === 'No' ? 'red' : 'amber'}`}>
                        {t.checklist_complete}
                      </span>
                    </td>
                    <td style={{display:'flex',gap:4}}>
                      <button className="btn-sm ghost" style={{padding:'3px 8px',fontSize:11}} onClick={() => editTrade(t)}>Edit</button>
                      <button className="btn-sm danger" style={{padding:'3px 8px',fontSize:11}} onClick={() => deleteTrade(t.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
